import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Pause, Play, Volume2, VolumeX, Waves, Circle } from "lucide-react";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";
import { getVoicePref } from "@/lib/voicePref";
import { isSoundEnabled } from "@/lib/chime";
import {
  startBreathAmbience, stopBreathAmbience, duckBreathAmbience,
} from "@/lib/breathAmbience";
import { scaleToTarget, totalMs, speakMs } from "@/lib/meditationVariants";

/**
 * VipassanaMeditation — a 10-minute guided Vipassana practice.
 *
 * Structured as a beat sequence of `speak` and `pause` steps. Every
 * spoken sentence is delivered in N8's soft meditation voice at rate
 * 0.78 with breath-level pauses on commas and periods; the sequencer
 * only advances after the utterance actually finishes, so the pacing is
 * truly the pacing of the voice — never estimated. Pause beats hold
 * silence for real practice space.
 *
 * The visual is deliberately austere — a still, softly pulsing ring —
 * because Vipassana is about noticing what is already happening rather
 * than following a breath count. The breathing ambience pad plays
 * underneath the whole session and ducks to a whisper on pause.
 *
 * Total nominal duration: ~10 minutes. Actual length varies slightly
 * with the device's TTS voice pace but stays within ±60 s.
 */

// Full script — ordered exactly as it should be experienced. Long pause
// beats in the middle are the meditation itself, not padding.
const BEATS = [
  // --- 1. Settling into posture ---
  { type: "speak", text: "Welcome. Find a comfortable seated posture. Sit tall, without strain." },
  { type: "pause", ms: 3000 },
  { type: "speak", text: "Let your hands rest gently. Let your shoulders drop. Soften the muscles of your face." },
  { type: "pause", ms: 4000 },
  { type: "speak", text: "Close your eyes, or lower your gaze softly toward the floor." },
  { type: "pause", ms: 5000 },
  { type: "speak", text: "This practice is called Vipassana. It means to see things as they are. There is nothing to do. Nothing to change. Only to notice.", skipOn: ["beginner"] },
  { type: "pause", ms: 5000, skipOn: ["beginner"] },

  // --- 2. Breath awareness ---
  { type: "speak", text: "Begin by bringing your attention to your breath. Do not control it. Simply observe it as it is." },
  { type: "pause", ms: 8000 },
  { type: "speak", text: "Notice the sensation of the breath. At the tip of the nose. Or the movement of the chest. Or the rise and fall of the belly." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "As you breathe in, note softly to yourself: rising. As you breathe out, note: falling." },
  { type: "pause", ms: 12000 },
  { type: "speak", text: "Rising… falling. Rising… falling." },
  { type: "pause", ms: 25000 },
  { type: "speak", text: "There is no need to do anything. You are simply noticing what is already happening." },
  { type: "pause", ms: 12000 },
  { type: "speak", text: "If the mind wanders — and it will — gently return. No judgment. No effort. Just come back to the breath." },
  { type: "pause", ms: 20000 },

  // --- 3. Body scan ---
  { type: "speak", text: "Now we begin to observe the body. Bring your attention to the crown of your head." },
  { type: "pause", ms: 5000 },
  { type: "speak", text: "Notice any sensation. Warmth. Tingling. Pressure. Or the absence of sensation. All of it is welcome." },
  { type: "pause", ms: 8000 },
  { type: "speak", text: "Slowly move your attention down to your forehead. Your eyes. Your jaw." },
  { type: "pause", ms: 8000 },
  { type: "speak", text: "Notice sensations as they arise. Notice them as they fade. Rising… falling." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Continue down through your neck. Your shoulders. Your chest." },
  { type: "pause", ms: 8000 },
  { type: "speak", text: "Take your time. There is no rush.", skipOn: ["beginner"] },
  { type: "pause", ms: 15000, skipOn: ["beginner"] },
  { type: "speak", text: "Move through your arms. Your hands. The tips of your fingers." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Then your back. Your belly. Your hips." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Down through your legs. Your feet. Your toes." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Notice without labeling as good or bad. Without wanting the sensation to stay. Without wanting it to leave." },
  { type: "pause", ms: 12000 },

  // --- 4. Mindful open observation ---
  { type: "speak", text: "Now open your awareness. Let go of any specific point of focus." },
  { type: "pause", ms: 5000 },
  { type: "speak", text: "Notice whatever arises in your experience. A sound. A thought. A feeling. A sensation in the body." },
  { type: "pause", ms: 20000 },
  { type: "speak", text: "Each one rises. Each one falls." },
  { type: "pause", ms: 20000 },
  { type: "speak", text: "You are not your thoughts. You are the awareness that observes them.", skipOn: ["beginner"] },
  { type: "pause", ms: 20000, skipOn: ["beginner"] },
  { type: "speak", text: "If the mind wanders, gently return to the breath. Rising… falling. Then open again." },
  { type: "pause", ms: 25000 },

  // --- 5. Whole-body awareness + return ---
  { type: "speak", text: "Now, expand your awareness to the whole body at once. Feel the entire field of sensation, held in stillness." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Every rising. Every falling. All held in the same quiet awareness." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Slowly begin to widen the awareness. Notice sounds around you. The temperature of the room. The support beneath you." },
  { type: "pause", ms: 6000 },
  { type: "speak", text: "Take one full breath. Deeper this time. And when you are ready, gently open your eyes." },
  { type: "pause", ms: 3000 },
  { type: "speak", text: "You are here. Nothing needs to be added. Nothing needs to be fixed. Just this. Just now." },
];

// Rough per-beat duration used only for the top progress bar. Speech
// beats are estimated from word count at N8's meditation pace.
function beatDurationMs(beat) {
  if (beat.type === "pause") return beat.ms || 0;
  if (beat.type === "speak") return speakMs(beat.text);
  return 0;
}

const TOTAL_MS = BEATS.reduce((acc, b) => acc + beatDurationMs(b), 0);

// Wall-clock targets by variant. `scaleToTarget` computes the required
// pause multiplier so the practice lands close to the target duration.
const TARGETS = {
  beginner:      5 * 60 * 1000,
  intermediate: 10 * 60 * 1000,
  advanced:     20 * 60 * 1000,
};

export default function VipassanaMeditation({ open, onClose, variant = "intermediate" }) {
  const target = TARGETS[variant] ?? TARGETS.intermediate;
  const beats = React.useMemo(() => scaleToTarget(BEATS, target, variant), [target, variant]);
  const TOTAL_MS_LOCAL = React.useMemo(() => totalMs(beats), [beats]);
  const [beatIdx, setBeatIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [narrating, setNarrating] = useState(() => canSpeak() && getVoicePref().enabled);
  const [ambience, setAmbience] = useState(() => isSoundEnabled());
  const [elapsedMs, setElapsedMs] = useState(0);
  const cancelledRef = useRef(false);
  const timeoutRef = useRef(null);

  // Reset all state when the overlay opens.
  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    setBeatIdx(0);
    setPaused(false);
    setElapsedMs(0);
  }, [open]);

  // Cleanup on close/unmount.
  useEffect(() => () => {
    cancelledRef.current = true;
    stopSpeaking();
    stopBreathAmbience();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Ambience lifecycle.
  useEffect(() => {
    if (!open) { stopBreathAmbience(); return undefined; }
    if (!ambience) { stopBreathAmbience(); return undefined; }
    startBreathAmbience();
    return () => { /* handled by open==false branch */ };
  }, [open, ambience]);

  useEffect(() => {
    if (!ambience || !open) return;
    duckBreathAmbience(paused ? 0.35 : 1.0, 0.6);
  }, [paused, ambience, open]);

  // Drive the beat sequence.
  useEffect(() => {
    if (!open || paused || cancelledRef.current) return undefined;
    const beat = beats[beatIdx];
    if (!beat) return undefined;
    let cleanup = () => {};

    const advance = () => {
      if (cancelledRef.current) return;
      setElapsedMs((ms) => ms + beatDurationMs(beat));
      if (beatIdx + 1 >= beats.length) return;   // finished — leave closing line up
      setBeatIdx(beatIdx + 1);
    };

    if (beat.type === "speak") {
      if (narrating && canSpeak()) {
        speak(beat.text, { context: "meditation", onEnd: advance });
        cleanup = () => stopSpeaking();
      } else {
        const dur = beatDurationMs(beat);
        timeoutRef.current = setTimeout(advance, dur);
        cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
      }
    } else if (beat.type === "pause") {
      timeoutRef.current = setTimeout(advance, beat.ms || 0);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    }

    return () => { cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beatIdx, open, paused, narrating]);

  useEffect(() => {
    if (paused) {
      stopSpeaking();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [paused]);

  // Respect the global N8 voice preference.
  useEffect(() => {
    const onPref = () => {
      const enabled = getVoicePref().enabled;
      if (!enabled) { setNarrating(false); stopSpeaking(); }
    };
    window.addEventListener("n8-voice-pref-changed", onPref);
    return () => window.removeEventListener("n8-voice-pref-changed", onPref);
  }, []);

  if (!open) return null;

  const beat = beats[beatIdx];
  const finished = beatIdx >= beats.length - 1 && !paused;
  const progress = Math.min(100, Math.round((elapsedMs / TOTAL_MS_LOCAL) * 100));
  const displayText = beat?.type === "speak" ? beat.text : "";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Vipassana meditation"
      data-testid="vipassana-overlay"
    >
      {/* Deep, dim ambient backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(11, 15, 19, 0.985)" }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(ellipse at 50% 42%, rgba(180,180,190,0.12) 0%, rgba(60,50,50,0.04) 45%, transparent 75%)",
        }}
      />

      {/* Progress bar */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5 bg-white/5"
        data-testid="vipassana-progress-track"
      >
        <div
          className="h-full bg-white/40 transition-[width] duration-700"
          style={{ width: `${progress}%` }}
          data-testid="vipassana-progress-bar"
        />
      </div>

      {/* Close — z-30 so it sits above the main stage's z-10 layer and
          actually receives pointer events. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close meditation"
        data-testid="vipassana-close"
        className="absolute top-4 right-4 z-30 rounded-full p-2.5 text-white/80 hover:text-white bg-white/5 hover:bg-white/15 tab-link"
      >
        <X size={18} />
      </button>

      {/* Main stage */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-8 text-center gap-10">
        <StillRing />

        <div className="max-w-2xl min-h-[3.5em]">
          <p
            key={beatIdx}
            className="font-display italic text-white/90 text-2xl md:text-3xl leading-relaxed animate-[fade-up_800ms_ease-out]"
            data-testid="vipassana-text"
          >
            {displayText}
          </p>
        </div>

        {/* Bottom controls */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? "Resume meditation" : "Pause meditation"}
            data-testid="vipassana-toggle"
            className="rounded-full p-2 text-white/80 hover:text-white hover:bg-white/10 tab-link"
            disabled={finished}
          >
            {paused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          {canSpeak() && (
            <button
              type="button"
              onClick={() => setNarrating((n) => !n)}
              aria-label={narrating ? "Mute N8's voice" : "Unmute N8's voice"}
              aria-pressed={narrating}
              data-testid="vipassana-narration-toggle"
              className={`rounded-full p-2 tab-link ${
                narrating
                  ? "text-white bg-white/15 hover:bg-white/25"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              }`}
              disabled={finished}
            >
              {narrating ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAmbience((a) => !a)}
            aria-label={ambience ? "Mute breathing ambience" : "Play breathing ambience"}
            aria-pressed={ambience}
            data-testid="vipassana-ambience-toggle"
            title={ambience ? "Ambience on" : "Ambience off"}
            className={`rounded-full p-2 tab-link ${
              ambience
                ? "text-white bg-white/15 hover:bg-white/25"
                : "text-white/60 hover:text-white hover:bg-white/10"
            }`}
            disabled={finished}
          >
            <Waves size={15} />
          </button>
        </div>

        {finished && (
          <div
            className="text-[11px] uppercase tracking-[0.28em] text-white/60"
            data-testid="vipassana-complete"
          >
            Meditation complete
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * StillRing — a quiet, self-breathing ring for a Vipassana practice.
 * Unlike the heart-coherence ring which follows a strict 5-in / 5-out
 * count, this one breathes very slowly on its own — a subtle 8-second
 * pulse — because Vipassana is about *observing* the breath, not
 * shaping it. The visual holds the field without asking anything.
 */
function StillRing() {
  return (
    <div className="relative grid place-items-center h-[300px] w-[300px]" data-testid="vipassana-ring">
      <div
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: 260,
          height: 260,
          animation: "vipassana-aura-pulse 8s ease-in-out infinite",
          background: "radial-gradient(circle, rgba(220,215,205,0.16) 0%, rgba(220,215,205,0) 65%)",
        }}
      />
      <div
        aria-hidden
        className="absolute rounded-full border border-white/40"
        style={{
          width: 200,
          height: 200,
          animation: "vipassana-ring-pulse 8s ease-in-out infinite",
        }}
      />
      <Circle
        aria-hidden
        size={10}
        strokeWidth={2}
        className="text-white/80 relative z-10"
        fill="currentColor"
      />
      {/* Keyframes injected once. */}
      <style>{`
        @keyframes vipassana-ring-pulse {
          0%, 100% { transform: scale(0.94); opacity: 0.65; }
          50%      { transform: scale(1.06); opacity: 1;    }
        }
        @keyframes vipassana-aura-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1;    }
        }
      `}</style>
    </div>
  );
}
